import { Avatar, Box, Flex, Switch, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { ViewOnSiteButton } from '../../../components/ViewOnSiteButton'
import {
  formatDateTime,
  useAppToast,
  useHasRole,
  usePendingRows,
  buildTeacherPreviewUrl,
  WEBSITE_URL
} from '../../../helpers'
import { TeacherService } from '../../../services/Teacher/TeacherService'
import { Teacher, TeacherComplete, TeachingFormat } from '../../../services/Teacher/types'
import { TeacherFormModal } from './TeacherFormModal'

const formats: Record<TeachingFormat, string> = {
  IN_PERSON: 'Presencial',
  LIVE_ONLINE: 'Online/Ao Vivo',
  RECORDED_ONLINE: 'Online/Gravado'
}

export default function TeacherPage() {
  const service = new TeacherService()
  const toast = useAppToast()
  const didMount = useRef(false)
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [targetRecord, setTargetRecord] = useState<Teacher>()
  const [filters, setFilters] = useState({ search: '', length: DEFAULT_PAGE_SIZE, status: 'all' })
  const isDirty = filters.search !== '' || filters.status !== 'all'

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<TeacherComplete>()
  const savingRow = usePendingRows()
  const hasRole = useHasRole()
  const permissions = {
    addTeacher: hasRole('add_teacher'),
    editTeacher: hasRole('edit_teacher'),
    toggleTeacherStatus: hasRole('toggle_teacher_status'),
    deleteTeacher: hasRole('delete_teacher')
  }

  // A busca já chega debounced do `FilterSearch`; o select de status aplica na hora.
  useEffect(() => {
    refreshList()
  }, [filters, page])

  useEffect(() => {
    if (didMount.current) {
      if (!isCreateModalOpen) {
        refreshList()
      }
    } else {
      didMount.current = true
    }
  }, [isCreateModalOpen])

  const refreshList = async () => {
    setLoading(true)
    setLoadError(false)
    service
      .list({ ...filters, page })
      .then((response) => {
        setTeachers(response.data)
        setTotalPages(Math.ceil(response.total_filtered / filters.length))
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os professores', error)
      })
      .finally(() => setLoading(false))
  }

  const handleDeleteTeacher = async () => {
    if (!targetRecord) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(targetRecord.id)
      toast.success('Professor excluído')
      setOpenDeleteDialog(false)
      refreshList()
    } catch (error) {
      toast.error('Erro ao excluir professor', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const openEditModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsCreateModalOpen(true)
    })
  }

  const toggleShowOnForms = async (teacher: Teacher, show_on_forms: boolean) => {
    try {
      await service.updateShowOnForms(teacher.id, show_on_forms)

      toast.success(`Professor ${show_on_forms ? 'inserido nos' : 'removido dos'} formulários`)

      setTeachers((prev) => prev.map((item) => (item.id === teacher.id ? { ...item, show_on_forms } : item)))
    } catch {
      toast.error('Não foi possível alterar a exibição do professor nos formulários')
    }
  }

  const toggleStatus = async (teacher: Teacher) => {
    try {
      await service.toggleStatus(teacher.id)

      toast.success(`Professor ${teacher.enabled ? 'inativado' : 'ativado'}`)

      setTeachers((prev) => prev.map((item) => (item.id === teacher.id ? { ...item, enabled: !item.enabled } : item)))
    } catch (error) {
      toast.error('Não foi possível alterar o status do professor', error)
    }
  }

  return (
    <>
      <PageTitle title="Professores" description="Cadastro dos professores exibidos no site" />

      <FilterBar onClear={() => updateFilters({ search: '', status: 'all' })} isDirty={isDirty}>
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="Buscar por nome"
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => updateFilters({ status })}
          options={[
            { value: 'active', label: 'Ativos' },
            { value: 'inactive', label: 'Inativos' }
          ]}
          // "Todos" aqui é um valor de verdade que a API espera, não a ausência de filtro.
          emptyValue="all"
        />
      </FilterBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Formatos</Th>
              <Th>Exibir nos forms</Th>
              <Th>Ativar/Inativar</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="professores" onRetry={refreshList} />
            ) : teachers.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="professor" />
            ) : (
              teachers.map((teacher) => (
                <Tr key={teacher.id}>
                  <Td>{teacher.id}</Td>
                  <Td>
                    <Flex alignItems="center" gap={2}>
                      <Avatar size="sm" src={teacher.profile_picture} name={teacher.name} />
                      {teacher.name}
                    </Flex>
                  </Td>
                  <Td>
                    <EmptyCell value={teacher.teaching_formats?.map((format) => formats[format]).join(', ')} />
                  </Td>
                  <Td>
                    <Flex alignItems="center" gap={2}>
                      <Switch
                        isChecked={teacher.show_on_forms}
                        onChange={(e) => savingRow.run(teacher.id, () => toggleShowOnForms(teacher, e.target.checked))}
                        isDisabled={!permissions.editTeacher || savingRow.isPending(teacher.id)}
                      />
                      {teacher.show_on_forms && !teacher.email && (
                        <Tooltip label="Professor sem e-mail cadastrado — ele não receberá a notificação de entrega de trabalho.">
                          <Box as="span" tabIndex={0} color="orange.400" aria-label="Professor sem e-mail cadastrado">
                            <Icon name="lni lni-warning" />
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                  </Td>
                  <Td>
                    <Switch
                      isChecked={teacher.enabled}
                      onChange={() => savingRow.run(teacher.id, () => toggleStatus(teacher))}
                      isDisabled={!permissions.toggleTeacherStatus || savingRow.isPending(teacher.id)}
                    />
                  </Td>
                  <Td>{formatDateTime(teacher.created_at)}</Td>
                  <Td>
                    <ViewOnSiteButton
                      href={
                        teacher.enabled
                          ? `${WEBSITE_URL}/nosso-ensino/professores/${teacher.slug}`
                          : buildTeacherPreviewUrl(WEBSITE_URL, teacher)
                      }
                      label={teacher.enabled ? 'Ver no site' : 'Abrir preview'}
                      isGhost={!teacher.enabled}
                    />
                    {permissions.editTeacher && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          openEditModal(teacher.id)
                        }}
                      />
                    )}
                    {permissions.deleteTeacher && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setTargetRecord(teacher)
                          setOpenDeleteDialog(true)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={filters.length}
        onPageSizeChange={(pageSize) => {
          setFilters({ ...filters, length: pageSize })
          setPage(1)
        }}
      />

      {targetRecord && (
        <ConfirmDialog
          isOpen={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeleteTeacher}
          action="delete"
          entity="professor"
          name={targetRecord.name}
          isLoading={isDeleting}
        />
      )}

      {permissions.addTeacher && (
        <ActionFab
          label="Adicionar professor"
          onClick={() => {
            setRecordEditing(undefined)
            setIsCreateModalOpen(true)
          }}
        />
      )}

      <TeacherFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        teacher={recordEditing}
      />
    </>
  )
}
